package com.dotpx.ambient

import kotlin.math.abs
import kotlin.math.max

/** Five-note dove phrase: amplitude, pitch contour, and seeded note variation. */
internal class DovePhraseState(private val random: SplitMix64) {
    private var doveNotePitch1 = 0.0
    private var doveNotePitch2 = 0.0
    private var doveNotePitch3 = 0.0
    private var doveNotePitch4 = 0.0
    private var doveNotePitch5 = 0.0

    fun envelope(time: Double): Double = when {
        time < 0.03 -> lerp(time, 0.00, 0.00, 0.03, 0.37)
        time < 0.09 -> lerp(time, 0.03, 0.37, 0.09, 0.46)
        time < 0.18 -> lerp(time, 0.09, 0.46, 0.18, 0.92)
        time < 0.21 -> lerp(time, 0.18, 0.92, 0.21, 1.00)
        time < 0.30 -> lerp(time, 0.21, 1.00, 0.30, 0.28)
        time < 0.33 -> lerp(time, 0.30, 0.28, 0.33, 0.35)
        time < 0.39 -> lerp(time, 0.33, 0.35, 0.39, 0.91)
        time < 0.42 -> lerp(time, 0.39, 0.91, 0.42, 0.74)
        time < 0.54 -> lerp(time, 0.42, 0.74, 0.54, 0.24)
        time < 0.58 -> lerp(time, 0.54, 0.24, 0.58, 0.00)
        time < 0.82 -> 0.0
        time < 0.85 -> lerp(time, 0.82, 0.00, 0.85, 0.41)
        time < 0.91 -> lerp(time, 0.85, 0.41, 0.91, 0.69)
        time < 1.03 -> lerp(time, 0.91, 0.69, 1.03, 0.51)
        time < 1.10 -> lerp(time, 1.03, 0.51, 1.10, 0.20)
        time < 1.14 -> lerp(time, 1.10, 0.20, 1.14, 0.00)
        time < 1.27 -> 0.0
        time < 1.30 -> lerp(time, 1.27, 0.00, 1.30, 0.28)
        time < 1.36 -> lerp(time, 1.30, 0.28, 1.36, 0.36)
        time < 1.52 -> lerp(time, 1.36, 0.36, 1.52, 0.35)
        time < 1.61 -> lerp(time, 1.52, 0.35, 1.61, 0.47)
        time < 1.67 -> lerp(time, 1.61, 0.47, 1.67, 0.11)
        time < 1.70 -> lerp(time, 1.67, 0.11, 1.70, 0.00)
        else -> 0.0
    }

    fun pitch(time: Double): Double = when {
        time < 0.03 -> lerp(time, 0.00, 0.909, 0.03, 0.895)
        time < 0.06 -> lerp(time, 0.03, 0.895, 0.06, 1.000)
        time < 0.09 -> lerp(time, 0.06, 1.000, 0.09, 1.077)
        time < 0.15 -> lerp(time, 0.09, 1.077, 0.15, 1.055)
        time < 0.21 -> lerp(time, 0.15, 1.055, 0.21, 0.984)
        time < 0.30 -> lerp(time, 0.21, 0.984, 0.30, 0.948)
        time < 0.39 -> lerp(time, 0.30, 0.948, 0.39, 1.009)
        time < 0.42 -> lerp(time, 0.39, 1.009, 0.42, 0.955)
        time < 0.54 -> lerp(time, 0.42, 0.955, 0.54, 0.984)
        time < 0.58 -> lerp(time, 0.54, 0.984, 0.58, 0.900)
        time < 0.82 -> lerp(time, 0.58, 0.900, 0.82, 0.859)
        time < 0.88 -> lerp(time, 0.82, 0.859, 0.88, 0.889)
        time < 1.10 -> lerp(time, 0.88, 0.889, 1.10, 0.850)
        time < 1.30 -> 0.850
        time < 1.36 -> lerp(time, 1.30, 0.855, 1.36, 0.841)
        time < 1.48 -> lerp(time, 1.36, 0.841, 1.48, 0.855)
        time < 1.52 -> lerp(time, 1.48, 0.855, 1.52, 0.873)
        time < 1.55 -> lerp(time, 1.52, 0.873, 1.55, 1.007)
        time < 1.58 -> lerp(time, 1.55, 1.007, 1.58, 1.043)
        time < 1.61 -> lerp(time, 1.58, 1.043, 1.61, 1.011)
        time < 1.67 -> lerp(time, 1.61, 1.011, 1.67, 1.023)
        else -> 1.023
    }

    fun notePitchMultiplier(time: Double): Double = 1.0 + when {
        time < 0.27 -> doveNotePitch1
        time < 0.34 -> smoothLerp(time, 0.27, doveNotePitch1, 0.34, doveNotePitch2)
        time < 0.62 -> doveNotePitch2
        time < 0.78 -> smoothLerp(time, 0.62, doveNotePitch2, 0.78, doveNotePitch3)
        time < 1.16 -> doveNotePitch3
        time < 1.25 -> smoothLerp(time, 1.16, doveNotePitch3, 1.25, doveNotePitch4)
        time < 1.47 -> doveNotePitch4
        time < 1.58 -> smoothLerp(time, 1.47, doveNotePitch4, 1.58, doveNotePitch5)
        else -> doveNotePitch5
    }

    fun randomize() {
        doveNotePitch1 = randomDoveNotePitch(0.0)
        doveNotePitch2 = randomDoveNotePitch(doveNotePitch1)
        doveNotePitch3 = randomDoveNotePitch(doveNotePitch2)
        doveNotePitch4 = randomDoveNotePitch(doveNotePitch3)
        doveNotePitch5 = randomDoveNotePitch(doveNotePitch4)
        val mean = (doveNotePitch1 + doveNotePitch2 + doveNotePitch3 +
            doveNotePitch4 + doveNotePitch5) / 5.0
        doveNotePitch1 -= mean
        doveNotePitch2 -= mean
        doveNotePitch3 -= mean
        doveNotePitch4 -= mean
        doveNotePitch5 -= mean
        val maximum = max(
            max(abs(doveNotePitch1), abs(doveNotePitch2)),
            max(max(abs(doveNotePitch3), abs(doveNotePitch4)), abs(doveNotePitch5)),
        )
        if (maximum > 0.065) {
            val scale = 0.065 / maximum
            doveNotePitch1 *= scale
            doveNotePitch2 *= scale
            doveNotePitch3 *= scale
            doveNotePitch4 *= scale
            doveNotePitch5 *= scale
        }
    }

    private fun randomDoveNotePitch(previous: Double): Double {
        var pitch = randomDouble(-0.055, 0.055)
        if (abs(pitch - previous) < 0.018) {
            pitch = if (pitch >= previous) pitch + 0.028 else pitch - 0.028
        }
        return pitch.coerceIn(-0.065, 0.065)
    }

    fun reset() {
        doveNotePitch1 = 0.0
        doveNotePitch2 = 0.0
        doveNotePitch3 = 0.0
        doveNotePitch4 = 0.0
        doveNotePitch5 = 0.0
    }

    private fun smoothLerp(
        time: Double,
        startTime: Double,
        startValue: Double,
        endTime: Double,
        endValue: Double,
    ): Double {
        val linear = ((time - startTime) / max(endTime - startTime, 0.000_001)).coerceIn(0.0, 1.0)
        val smooth = linear * linear * (3.0 - 2.0 * linear)
        return startValue + (endValue - startValue) * smooth
    }

    private fun lerp(time: Double, startTime: Double, startValue: Double, endTime: Double, endValue: Double): Double {
        val amount = (time - startTime) / max(endTime - startTime, 0.000_001)
        return startValue + (endValue - startValue) * amount
    }

    private fun randomDouble(low: Double, high: Double): Double =
        low + (high - low) * random.nextUnitDouble()
}
